/**
 * Offline outbox: registrations and walk-in visits captured while the server is unreachable.
 *
 * Each item is replayed in order once the connection returns, using Idempotency-Key headers so a
 * retry after a dropped connection never creates a second patient or a second queue token. Items
 * that the server flags as a possible duplicate wait for a person to decide; nothing is merged or
 * overridden automatically. Items belong to the user and hospital that created them and are only
 * sent by that user.
 */
import { ApiError, request } from '@/lib/api'
import { outboxStore, seal, unseal, type Sealed } from './store'

export interface VisitDraft {
  department_id: string
  department_name: string
  visit_reason: string | null
  service_item_id: string | null
  payer_type: string
  priority: number
}

export interface Candidate {
  patient: { id: string; mrn: string; full_name: string; sex: string; age: number | null; phone: string | null }
  score: number
  reasons: string[]
}

/** Decrypted content of an item. Contains patient details — never persisted unencrypted. */
export interface OutboxData {
  /** Registration payload for POST /patients (registration items). */
  patient?: Record<string, unknown>
  /** Known MRN (walk-in for an existing patient). */
  mrn?: string
  label: string
  visit: VisitDraft | null
  confirmNew?: boolean
  candidates?: Candidate[]
  result?: { mrn?: string; full_name?: string; token_no?: number; encounter_id?: string }
}

export type OutboxStatus = 'pending' | 'syncing' | 'needs_review' | 'failed' | 'done'

/** What is stored: routing metadata in clear, everything about the patient sealed. */
export interface OutboxItem {
  id: string
  kind: 'registration' | 'walk_in'
  tenantId: string
  userId: string
  createdAt: string
  provisional: string
  status: OutboxStatus
  attempts: number
  lastError: string | null
  patientId: string | null
  doneAt: string | null
  sealed: Sealed
}

export interface Owner {
  tenantId: string
  userId: string
}

type Listener = () => void
const listeners = new Set<Listener>()
export function subscribe(fn: Listener) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
const emit = () => listeners.forEach((l) => l())

function nextProvisional(): string {
  const today = new Date().toISOString().slice(0, 10)
  let n = 1
  try {
    const raw = JSON.parse(localStorage.getItem('lh:offline-seq') ?? 'null') as { d: string; n: number } | null
    if (raw && raw.d === today) n = raw.n + 1
    localStorage.setItem('lh:offline-seq', JSON.stringify({ d: today, n }))
  } catch {
    n = Math.floor(Math.random() * 900) + 100
  }
  return `OFF-${String(n).padStart(3, '0')}`
}

export async function enqueue(owner: Owner, kind: OutboxItem['kind'], data: OutboxData): Promise<OutboxItem> {
  const item: OutboxItem = {
    id: crypto.randomUUID(),
    kind,
    ...owner,
    createdAt: new Date().toISOString(),
    provisional: nextProvisional(),
    status: 'pending',
    attempts: 0,
    lastError: null,
    patientId: null,
    doneAt: null,
    sealed: await seal(data),
  }
  await outboxStore.put(item)
  emit()
  return item
}

export async function listAll(): Promise<OutboxItem[]> {
  const rows = await outboxStore.all<OutboxItem>()
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function listMine(owner: Owner): Promise<OutboxItem[]> {
  return (await listAll()).filter((i) => i.tenantId === owner.tenantId && i.userId === owner.userId)
}

export const read = (item: OutboxItem) => unseal<OutboxData>(item.sealed)

async function save(item: OutboxItem, data?: OutboxData) {
  if (data) item.sealed = await seal(data)
  await outboxStore.put(item)
  emit()
}

export async function discard(id: string) {
  await outboxStore.remove(id)
  emit()
}

/** Remove confirmed items older than `hours` (they are kept briefly so staff can map OFF- slips to tokens). */
export async function purgeDone(hours = 12) {
  const cutoff = Date.now() - hours * 3600_000
  for (const i of await listAll()) if (i.status === 'done' && i.doneAt && Date.parse(i.doneAt) < cutoff) await outboxStore.remove(i.id)
  emit()
}

/** A person resolved a possible duplicate: use the existing record, or confirm it is a new patient. */
export async function resolveReview(id: string, choice: { existingPatientId: string } | { registerNew: true }) {
  const item = await outboxStore.get<OutboxItem>(id)
  if (!item) return
  const data = await read(item)
  if ('existingPatientId' in choice) {
    const c = data.candidates?.find((x) => x.patient.id === choice.existingPatientId)
    item.patientId = choice.existingPatientId
    data.result = { ...data.result, mrn: c?.patient.mrn, full_name: c?.patient.full_name }
  } else data.confirmNew = true
  data.candidates = undefined
  item.status = 'pending'
  item.lastError = null
  await save(item, data)
}

export async function retry(id: string) {
  const item = await outboxStore.get<OutboxItem>(id)
  if (!item) return
  item.status = 'pending'
  item.lastError = null
  await save(item)
}

class Offline extends Error {}

async function call<T>(path: string, body: unknown, key: string, query?: Record<string, string>): Promise<T> {
  try {
    return await request<T>(path, body === undefined ? { query } : { method: 'POST', body, headers: { 'Idempotency-Key': key } })
  } catch (e) {
    if (e instanceof ApiError && (e.status === 0 || e.status >= 500)) throw new Offline(e.message)
    throw e
  }
}

async function processItem(item: OutboxItem): Promise<void> {
  const data = await read(item)
  item.attempts += 1
  item.status = 'syncing'
  await save(item)
  try {
    if (!item.patientId) {
      if (item.kind === 'registration') {
        const p = await call<{ id: string; mrn: string; full_name: string }>('/patients', { ...data.patient, confirm_not_duplicate: !!data.confirmNew }, `${item.id}-patient`)
        item.patientId = p.id
        data.result = { ...data.result, mrn: p.mrn, full_name: p.full_name }
      } else {
        const page = await call<{ items: { id: string; mrn: string; full_name: string }[] }>('/patients', undefined, '', { q: data.mrn ?? '' })
        const match = page.items.find((x) => x.mrn.toUpperCase() === (data.mrn ?? '').toUpperCase())
        if (!match) {
          item.status = 'failed'
          item.lastError = `No patient with MRN ${data.mrn} was found. Check the number or register the patient.`
          return save(item, data)
        }
        item.patientId = match.id
        data.result = { ...data.result, mrn: match.mrn, full_name: match.full_name }
      }
      await save(item, data)
    }
    if (data.visit && data.result?.token_no === undefined) {
      const v = data.visit
      const r = await call<{ token_no: number; encounter_id: string }>(
        '/visits/walk-in',
        { patient_id: item.patientId, department_id: v.department_id, visit_reason: v.visit_reason, service_item_id: v.service_item_id, payer_type: v.payer_type, priority: v.priority, type: 'opd' },
        `${item.id}-visit`,
      )
      data.result = { ...data.result, token_no: r.token_no, encounter_id: r.encounter_id }
    }
    item.status = 'done'
    item.doneAt = new Date().toISOString()
    item.lastError = null
    await save(item, data)
  } catch (e) {
    if (e instanceof Offline) {
      item.status = 'pending'
      item.lastError = 'Waiting for the connection.'
      await save(item, data)
      throw e
    }
    if (e instanceof ApiError && e.code === 'possible_duplicates') {
      item.status = 'needs_review'
      item.lastError = 'This patient may already be registered. Choose the existing record or confirm a new one.'
      data.candidates = e.details as Candidate[]
      return save(item, data)
    }
    item.status = 'failed'
    item.lastError = e instanceof ApiError ? e.message : 'Could not be sent.'
    await save(item, data)
  }
}

let running: Promise<number> | null = null

/** Send the current user's pending items in order. Returns how many were confirmed. Single-flight. */
export function syncOutbox(owner: Owner): Promise<number> {
  if (running) return running
  running = (async () => {
    let done = 0
    try {
      for (const item of await listMine(owner)) {
        if (item.status !== 'pending' && item.status !== 'syncing') continue
        await processItem(item)
        if ((item.status as OutboxStatus) === 'done') done += 1
      }
    } catch (e) {
      if (!(e instanceof Offline)) throw e
    } finally {
      running = null
    }
    return done
  })()
  return running
}
